import { PropsWithChildren, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

const productName = 'Ventris Learning';

function routeName(pathname: string, portal: string | null): string {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] || '';

  if (pathname === '/') {
    if (portal === 'admin') {
      return 'Administrator Dashboard';
    }

    if (portal === 'clinician') {
      return 'Dashboard';
    }

    return 'Login';
  }

  const staticRouteNames: Record<string, string> = {
    admin: 'Administrator Login',
    onboarding: 'New Account Registration',
    'reset-password': 'Reset Password',
    join: 'Start Student Test',
    organizations: 'Directory',
    users: 'Users',
    students: 'Students',
    classes: 'Classes',
    groups: 'Groups',
    tests: 'Tests',
    results: 'Results',
  };

  if (staticRouteNames[firstSegment]) {
    return staticRouteNames[firstSegment];
  }

  if (firstSegment === 'sso') {
    return 'Completing SSO Sign In';
  }

  if (firstSegment === 'student') {
    return 'Student Details';
  }

  if (firstSegment === 'class') {
    return 'Class Details';
  }

  if (firstSegment === 'group') {
    return 'Group Details';
  }

  if (firstSegment === 'report') {
    return 'Student Report';
  }

  if (firstSegment === 'test' || firstSegment === 'student-test') {
    const assessmentName = (segments[1] || 'Assessment').toUpperCase();
    return `${assessmentName} Test`;
  }

  return productName;
}

export function titleForRoute(pathname: string, portal: string | null = null): string {
  const name = routeName(pathname, portal);
  return name === productName ? productName : `${name} | ${productName}`;
}

function AccessibilityEffects() {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  useEffect(() => {
    document.title = titleForRoute(location.pathname, sessionStorage.getItem('portal'));

    if (previousPath.current !== location.pathname) {
      document.getElementById('main-content')?.focus();
    }

    previousPath.current = location.pathname;
  }, [location.pathname]);

  return null;
}

export function RouteAccessibilityFrame({ children }: PropsWithChildren) {
  const focusMainContent = () => {
    document.getElementById('main-content')?.focus();
  };

  return (
    <>
      <AccessibilityEffects />
      <a className="skip-link" href="#main-content" onClick={focusMainContent}>Skip to main content</a>
      {children}
    </>
  );
}

function RouteAccessibility() {
  return (
    <RouteAccessibilityFrame>
      <Outlet />
    </RouteAccessibilityFrame>
  );
}

export default RouteAccessibility;
